import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const STEPS = [
  {
    n: "01",
    title: "Analisi",
    duration: "2 – 3 settimane",
    body:
      "Entriamo in azienda e leggiamo i numeri reali. Bilanci, gestionale, processi, organigramma, posizionamento. Non lavoriamo su deck, lavoriamo sui dati.",
    activities: [
      "Audit di bilancio e controllo di gestione",
      "Interviste a proprietà, management e key people",
      "Mappatura processi critici e bottleneck operativi",
      "Benchmark di settore con dati pubblici e proprietari",
    ],
  },
  {
    n: "02",
    title: "Strategia",
    duration: "2 settimane",
    body:
      "Sintetizziamo le evidenze in tre o quattro priorità strategiche. Definiamo gli obiettivi numerici, le leve e le responsabilità. Scartiamo tutto ciò che non sposta il business.",
    activities: [
      "Tre scenari quantitativi a 36 mesi",
      "Selezione delle leve di crescita prioritarie",
      "Definizione KPI misurabili per area",
      "Allineamento con la proprietà su rischi e trade-off",
    ],
  },
  {
    n: "03",
    title: "Implementazione",
    duration: "3 – 9 mesi",
    body:
      "I partner restano dentro l'azienda fianco a fianco con il team. Trasformiamo la strategia in operatività: piano d'azione, milestone, riunioni di avanzamento, escalation.",
    activities: [
      "Piano d'azione settimanale con responsabili",
      "Affiancamento operativo a CFO / responsabili commerciali / produzione",
      "Gestione delle pratiche di finanza agevolata abilitanti",
      "Comitato di steering bisettimanale con la proprietà",
    ],
  },
  {
    n: "04",
    title: "Monitoraggio",
    duration: "Continuativo · trimestrale",
    body:
      "Trimestre dopo trimestre revisioniamo i numeri attesi rispetto a quelli reali. Se la traiettoria scarta, correggiamo. La consulenza non finisce alla consegna del piano.",
    activities: [
      "Reportistica mensile di KPI strategici",
      "Revisione trimestrale piano vs. consuntivo",
      "Ricalibratura su scostamenti > 10%",
      "Aggiornamento del piano industriale annuale",
    ],
  },
];

export default function Approach() {
  useReveal();
  return (
    <div data-testid="page-approach">
      <section className="bv-container pt-20 md:pt-28 pb-20">
        <p className="overline reveal">/ Approccio</p>
        <h1 className="h-display text-5xl md:text-7xl mt-6 max-w-[20ch] reveal">
          Un metodo strutturato.
          <br />
          <span className="text-[#2BA8AD]">Quattro fasi non negoziabili.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-[17px] text-[#0E2E4D]/70 reveal">
          Lo stesso processo applicato a una manifattura da 8 milioni e a un gruppo da 80. Cambia la profondità,
          non la sequenza. È così che garantiamo coerenza dei risultati.
        </p>
      </section>

      <section className="border-t border-[#0E2E4D]/15">
        {STEPS.map((s, i) => (
          <article
            key={s.n}
            data-testid={`approach-detail-${s.n}`}
            className="border-b border-[#0E2E4D]/15"
          >
            <div className="bv-container py-20 md:py-28 grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-3 reveal">
                <p className="font-mono text-[64px] md:text-[88px] leading-none text-[#0E2E4D]" style={{ fontWeight: 500 }}>
                  {s.n}
                </p>
                <p className="mt-4 font-mono text-[11px] tracking-[0.22em] uppercase text-[#2BA8AD]">{s.duration}</p>
              </div>
              <div className="col-span-12 md:col-span-9 reveal" style={{ "--reveal-delay": "120ms" }}>
                <h2 className="h-display text-5xl md:text-6xl">{s.title}</h2>
                <p className="mt-6 text-[17px] text-[#0E2E4D]/80 leading-relaxed max-w-prose">{s.body}</p>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                  {s.activities.map((a, idx) => (
                    <div
                      key={idx}
                      className="py-3 border-t border-[#0E2E4D]/15 flex items-baseline gap-3 text-[15px] text-[#0E2E4D]/85"
                    >
                      <span className="font-mono text-[10px] tracking-[0.22em] text-[#2BA8AD]">/ {String(idx + 1).padStart(2, "0")}</span>
                      <span>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="bg-[#0E2E4D] text-[#F5F2EB]">
        <div className="bv-container py-24 md:py-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8 reveal">
            <h2 className="h-display text-4xl md:text-6xl leading-[1] max-w-[20ch]">
              Vuoi capire se il nostro metodo è adatto alla tua azienda?
            </h2>
            <p className="mt-6 text-[17px] text-[#F5F2EB]/75 max-w-2xl">
              In una call di 30 minuti scopriamo se siamo le persone giuste per il tuo prossimo passaggio di crescita.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end items-end reveal">
            <Link
              to="/contatti"
              data-testid="approach-cta-final"
              className="inline-flex items-center gap-3 bg-[#F5F2EB] text-[#0E2E4D] px-8 py-5 text-[12px] tracking-[0.18em] uppercase font-mono hover:bg-[#2BA8AD] hover:text-[#F5F2EB] transition-colors"
            >
              Richiedi una consulenza <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
